import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import LandingNav from "../components/landing/LandingNav";
import Footer from "../components/landing/Footer";
import { api, fmtErr } from "../lib/api";
import { IS_PREVIEW_HOST } from "../lib/env";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

export default function RequestAccess() {
  const [form, setForm] = useState({ full_name: "", company_name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (IS_PREVIEW_HOST) return; // en preview el formulario público está bloqueado
    setError("");
    setLoading(true);
    try {
      await api.post("/pilot-requests", form);
      setSent(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="request-access-page">
      <LandingNav />
      <main className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="max-w-xl mx-auto"
        >
          {IS_PREVIEW_HOST ? (
            <div className="bg-white rounded-3xl border border-amber-200 card-shadow p-10 text-center" data-testid="pilot-preview-block">
              <p className="text-xs uppercase tracking-widest font-medium text-amber-600 mb-5">Programa piloto</p>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">
                Este entorno es de pruebas
              </h1>
              <p className="text-zinc-600 leading-relaxed mt-4">
                Entorno de pruebas — las solicitudes reales deben realizarse en assetpassport.io
              </p>
            </div>
          ) : sent ? (
            <div className="bg-white rounded-3xl border border-zinc-200/80 card-shadow p-10 text-center" data-testid="pilot-success">
              <span className="mx-auto h-14 w-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
                <CheckCircle2 size={26} />
              </span>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">
                Solicitud recibida
              </h1>
              <p className="text-zinc-600 leading-relaxed mt-4">
                Nos pondremos en contacto contigo para darte acceso a Asset Passport.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <p className="text-xs uppercase tracking-widest font-medium text-zinc-500 mb-5">Programa piloto</p>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-950 leading-[1.08]">
                  Prueba Asset Passport en tu empresa
                </h1>
                <p className="text-lg text-zinc-600 leading-relaxed mt-5">
                  Estamos incorporando empresas a nuestro programa piloto. Déjanos tus datos y nos
                  pondremos en contacto contigo para darte acceso.
                </p>
              </div>

              <form
                onSubmit={submit}
                className="bg-white rounded-3xl border border-zinc-200/80 card-shadow p-7 sm:p-8 space-y-4"
                data-testid="pilot-request-form"
              >
                <input
                  className={inputCls}
                  placeholder="Nombre y apellidos *"
                  value={form.full_name}
                  onChange={set("full_name")}
                  required
                  maxLength={120}
                  autoComplete="name"
                  data-testid="pilot-name-input"
                />
                <input
                  className={inputCls}
                  placeholder="Empresa *"
                  value={form.company_name}
                  onChange={set("company_name")}
                  required
                  maxLength={160}
                  autoComplete="organization"
                  data-testid="pilot-company-input"
                />
                <input
                  className={inputCls}
                  type="email"
                  placeholder="Email profesional *"
                  value={form.email}
                  onChange={set("email")}
                  required
                  autoComplete="email"
                  data-testid="pilot-email-input"
                />
                <input
                  className={inputCls}
                  type="tel"
                  placeholder="Teléfono"
                  value={form.phone}
                  onChange={set("phone")}
                  maxLength={40}
                  autoComplete="tel"
                  data-testid="pilot-phone-input"
                />
                <textarea
                  className={`${inputCls} h-28 py-3 resize-none`}
                  placeholder="Mensaje"
                  value={form.message}
                  onChange={set("message")}
                  maxLength={2000}
                  data-testid="pilot-message-input"
                />

                <p className="text-xs text-zinc-500 leading-relaxed border-t border-zinc-100 pt-4" data-testid="pilot-privacy-layer">
                  <span className="font-medium text-zinc-700">Responsable:</span> Roberto Feltrer Valero (Asset Passport).{" "}
                  <span className="font-medium text-zinc-700">Finalidad:</span> gestionar tu solicitud de acceso al programa piloto y contactar contigo en relación con Asset Passport.{" "}
                  <span className="font-medium text-zinc-700">Base jurídica:</span> aplicación de medidas precontractuales solicitadas por ti.{" "}
                  Puedes consultar información adicional sobre el tratamiento de tus datos en nuestra{" "}
                  <Link to="/privacy" className="underline underline-offset-2 hover:text-zinc-900 transition-colors">
                    Política de privacidad
                  </Link>
                  .
                </p>

                {error && (
                  <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2.5" data-testid="pilot-error">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="pilot-submit-button"
                  className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
                >
                  {loading ? "Enviando…" : "Solicitar acceso"}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
